"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CacheManagerProvider = exports.CacheManager = void 0;
const debug_logger_1 = require("../../utils/debug-logger");
class CacheManager {
    constructor() {
        this.debugLogger = new debug_logger_1.DebugLogger('CacheManager');
        this.cacheStorage = {};
        this.debugLogger.on('stats', () => this.getCacheStats());
    }
    get(key) {
        if (!key) {
            this.debugLogger.warn(`GET operation requested with empty key (${key}), operation aborted`);
            return undefined;
        }
        this.debugLogger.log(`GET operation requested for key: "${key}"`);
        const cacheObject = this.cacheStorage[key];
        if (!cacheObject) {
            this.debugLogger.log(`GET result: Cache miss for key: "${key}" - no cache entry found`);
            return undefined;
        }
        const now = new Date();
        if (cacheObject.expiresAt && cacheObject.expiresAt <= now) {
            this.debugLogger.log(`GET result: Cache expired for key: "${key}" \
- expired at ${cacheObject.expiresAt.toISOString()}, \
current time: ${now.toISOString()}`);
            delete this.cacheStorage[key];
            return undefined;
        }
        if (cacheObject.data instanceof Promise) {
            this.debugLogger.log(`GET result: Cache hit (pending Promise) for key: "${key}"`);
        }
        else {
            this.debugLogger.log(`GET result: Cache hit for key: "${key}" - \
  cached at ${cacheObject.cachedAt.toISOString()}${cacheObject.expiresAt ? ', expires at ' + cacheObject.expiresAt.toISOString() : ' (no expiration)'}`);
        }
        return cacheObject.data;
    }
    set(key, data, ttl) {
        if (!key) {
            this.debugLogger.warn(`SET operation requested with empty key (${key}), operation aborted`);
            return;
        }
        this.debugLogger.log(`SET operation requested for key: "${key}" with ` + (ttl ? `TTL: ${ttl}ms` : 'no expiration'));
        const isNewEntry = !this.cacheStorage[key];
        const now = new Date();
        const cacheObject = {
            data,
            cachedAt: now,
            expiresAt: ttl ? new Date(now.getTime() + ttl) : undefined
        };
        this.cacheStorage[key] = cacheObject;
        this.debugLogger.log(`SET result: ${isNewEntry ? 'Created new' : 'Updated existing'} cache entry for key: "${key}" \
- cached at ${now.toISOString()}${cacheObject.expiresAt ? ', expires at ' + cacheObject.expiresAt.toISOString() : ' (no expiration)'}`);
    }
    delete(key) {
        if (!key) {
            this.debugLogger.warn(`DELETE operation requested with empty key (${key}), operation aborted`);
            return;
        }
        if (Array.isArray(key)) {
            this.debugLogger.log(`DELETE operation requested for multiple keys: [${key.join(', ')}]`);
            const existingKeys = key.filter(k => this.cacheStorage[k]);
            key.forEach(k => delete this.cacheStorage[k]);
            this.debugLogger.log(`DELETE result: Removed ${existingKeys.length}/${key.length} cache entries \
- existing keys: [${existingKeys.join(', ')}]`);
        }
        else {
            this.debugLogger.log(`DELETE operation requested for key: "${key}"`);
            const existed = !!this.cacheStorage[key];
            delete this.cacheStorage[key];
            this.debugLogger.log(`DELETE result: ${existed ? 'Removed' : 'Key not found for'} cache entry: "${key}"`);
        }
        this.debugLogger.log(`Current cache size: ${Object.keys(this.cacheStorage).length} entries`);
    }
    clearExpired() {
        const now = new Date();
        const totalKeys = Object.keys(this.cacheStorage);
        this.debugLogger.log(`CLEAR_EXPIRED operation requested - checking ${totalKeys.length} \
cache entries at ${now.toISOString()}`);
        const expiredKeys = [];
        totalKeys.forEach(key => {
            const cacheObject = this.cacheStorage[key];
            if (cacheObject.expiresAt && cacheObject.expiresAt <= now) {
                expiredKeys.push(key);
                delete this.cacheStorage[key];
            }
        });
        this.debugLogger.log(`CLEAR_EXPIRED result: Removed ${expiredKeys.length}/${totalKeys.length} expired entries${expiredKeys.length > 0 ? ` - keys: [${expiredKeys.join(', ')}]` : ''}`);
    }
    clearAll() {
        const totalKeys = Object.keys(this.cacheStorage).length;
        this.debugLogger.log(`CLEAR_ALL operation requested - clearing ${totalKeys} cache entries`);
        this.cacheStorage = {};
        this.debugLogger.log(`CLEAR_ALL result: Successfully cleared all ${totalKeys} cache entries`);
    }
    getCacheStats() {
        const keys = Object.keys(this.cacheStorage);
        const now = new Date();
        const expiredKeys = keys.filter(key => {
            const obj = this.cacheStorage[key];
            return obj.expiresAt && obj.expiresAt <= now;
        });
        const activeKeys = keys.filter(key => {
            const obj = this.cacheStorage[key];
            return !obj.expiresAt || obj.expiresAt > now;
        });
        return {
            activeKeys,
            expiredKeys,
            totalEntries: keys.length,
            activeEntries: activeKeys.length,
            expiredEntries: expiredKeys.length
        };
    }
}
exports.CacheManager = CacheManager;
const SHARED_CACHE_MANAGER_SYMBOL = Symbol.for('CacheManager');
class CacheManagerProvider {
    static getInstance() {
        if (!window[SHARED_CACHE_MANAGER_SYMBOL]) {
            window[SHARED_CACHE_MANAGER_SYMBOL] = new CacheManager();
        }
        return window[SHARED_CACHE_MANAGER_SYMBOL];
    }
}
exports.CacheManagerProvider = CacheManagerProvider;
